import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Loader2, Send, Trash2, RefreshCw } from 'lucide-react';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };

function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[10px] uppercase leading-tight" style={{ color: C.slate }}>{label}</p>
    </div>
  );
}

export const EmailGuardCard = () => {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [g, setG] = useState(null);
  const [held, setHeld] = useState(null);
  const [form, setForm] = useState({ value: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    Promise.all([api.get('/email/guard'), api.get('/email/guard/blocked', { params: { limit: 25 } })])
      .then(([a, b]) => { setG(a.data); setHeld(b.data); })
      .catch(() => setErr('Failed to load the outbound guard'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true); setErr(''); setMsg('');
    try { const r = await fn(); setMsg(typeof ok === 'function' ? ok(r?.data) : ok); load(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Request failed'); }
    setBusy(false);
  };

  if (!g) return null;
  const exposure = g.exposure || {};
  const budget = g.budget || {};
  const busiest = budget.busiest_recipients || [];

  return (
    <Card className="rounded-sm border-l-4 bg-white p-5 space-y-3" data-testid="email-guard-card"
      style={{ borderLeftColor: held?.total ? C.amber : C.green }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" style={{ color: C.green }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Outbound Guard — deny by default</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" data-testid="guard-mode-chip"
            style={{ background: 'rgba(29,158,117,0.1)', color: C.green }}>ARMED · NO OFF SWITCH</span>
          <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" data-testid="guard-cap-chip"
            style={{ background: 'rgba(26,76,124,0.1)', color: C.blue }}>
            MAX {budget.cap_per_recipient ?? '—'} PER RECIPIENT / {budget.window_hours ?? 24}H
          </span>
          <button onClick={load} className="p-1 rounded hover:bg-slate-50" style={{ color: C.slate }} data-testid="guard-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <p className="text-xs" style={{ color: C.slate }}>
        The book carries seeded broker accounts on fabricated domains and 300K+ enriched entity addresses that belong to real businesses.
        No email leaves the platform unless its recipient is on the list below — anything else is <b>held with its full body</b> and released only after you allow the recipient. Every change is audit-logged.
        Allowed recipients are metered too: no one hears from us more than <b>{budget.cap_per_recipient ?? 3} times in {budget.window_hours ?? 24} hours</b>, and never the same message twice.
      </p>

      <div className="flex gap-5 flex-wrap">
        <Stat label="allowlist entries" value={(g.allowlist || []).length} color={C.navy} testid="guard-count-allow" />
        <Stat label="held — awaiting release" value={g.held ?? 0} color={(g.held ?? 0) ? C.amber : C.green} testid="guard-count-held" />
        <Stat label="throttled — over inbox budget" value={g.throttled ?? 0} color={(g.throttled ?? 0) ? C.amber : C.green} testid="guard-count-throttled" />
        <Stat label="released after review" value={g.released ?? 0} color={C.blue} testid="guard-count-released" />
        <Stat label="reached before the guard" value={exposure.recipients_already_emailed ?? 0} color={C.red} testid="guard-count-exposure" />
      </div>

      <div data-testid="guard-busiest">
        <h4 className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>
          Busiest recipients — last {budget.window_hours ?? 24} hours
        </h4>
        {busiest.length === 0 ? (
          <p className="text-xs" style={{ color: C.green }} data-testid="guard-busiest-empty">
            No one has heard from us in the last {budget.window_hours ?? 24} hours.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {busiest.map(b => (
              <div key={b.to} className="px-2 py-1 rounded-sm border text-[11px]" data-testid={`guard-busiest-${b.to}`}
                style={{ borderColor: b.remaining === 0 ? 'rgba(232,160,32,0.5)' : 'rgba(136,150,168,0.25)',
                         background: b.remaining === 0 ? 'rgba(232,160,32,0.07)' : 'transparent' }}>
                <span className="font-mono" style={{ color: C.navy }}>{b.to}</span>
                <span style={{ color: b.remaining === 0 ? C.amber : C.slate }}>
                  {' · '}{b.sent} sent · {b.remaining} left
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {(exposure.recipients_already_emailed ?? 0) > 0 && (
        <p className="text-[11px] p-2 rounded-sm" data-testid="guard-exposure-note" style={{ background: 'rgba(226,75,74,0.07)', color: '#B03230' }}>
          {exposure.recipients_already_emailed} recipients were emailed before the guard existed and would be blocked today — e.g. {(exposure.sample || []).slice(0, 3).join(', ')}. Nothing further can reach them.
        </p>
      )}

      <div>
        <h4 className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>Allowed recipients</h4>
        <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="guard-allowlist-table">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">Value</th><th className="py-1 pr-2">Kind</th><th className="py-1 pr-2">Why</th><th className="py-1 pr-2">Added</th><th className="py-1"></th>
          </tr></thead>
          <tbody>
            {(g.allowlist || []).map(e => (
              <tr key={e.entry_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`guard-entry-${e.value}`}>
                <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>{e.value}{e.kind === 'domain' ? ' (+subdomains)' : ''}</td>
                <td className="py-1.5 pr-2" style={{ color: C.slate }}>{e.kind}</td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>{e.reason || '—'}</td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>{e.added_by} · {String(e.added_at).slice(0, 10)}</td>
                <td className="py-1.5 text-right">
                  {isAdmin && !e.protected && (
                    <button onClick={() => run(() => api.delete(`/email/guard/allow/${e.entry_id}`), `${e.value} removed — nothing can reach it now`)}
                      className="text-[11px]" style={{ color: C.red }} data-testid={`guard-remove-${e.value}`}>
                      <Trash2 className="w-3 h-3 inline" /> remove
                    </button>
                  )}
                  {e.protected && <span className="text-[10px]" style={{ color: C.slate }}>protected</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Input value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
              placeholder="name@client.com.au or client.com.au" className="rounded-sm text-xs h-8 w-64" data-testid="guard-allow-input" />
            <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Who they are / why they may be emailed" className="rounded-sm text-xs h-8 w-80" data-testid="guard-allow-reason" />
            <Button size="sm" disabled={busy || !form.value.trim()} className="rounded-sm text-xs" style={{ background: C.navy }}
              data-testid="guard-allow-btn"
              onClick={() => run(() => api.post('/email/guard/allow', form),
                d => `${form.value} allowed${d?.held_now_releasable ? ` — ${d.held_now_releasable} held email(s) can now be released` : ''}`)
                .then(() => setForm({ value: '', reason: '' }))}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Allow recipient'}
            </Button>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>Held email ({held?.total ?? 0})</h4>
        {(held?.rows || []).length === 0 ? (
          <p className="text-xs" style={{ color: C.green }} data-testid="guard-held-empty">Nothing held — every send attempt so far had an allowed recipient and sat inside its inbox budget.</p>
        ) : (
          <table className="w-full text-xs" data-testid="guard-held-table">
            <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
              <th className="py-1 pr-2">Intended recipient</th><th className="py-1 pr-2">Held as</th><th className="py-1 pr-2">Subject</th><th className="py-1 pr-2">Why held</th><th className="py-1 pr-2">When</th><th className="py-1"></th>
            </tr></thead>
            <tbody>
              {(held.rows || []).map(r => (
                <tr key={r.log_id} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`guard-held-${r.log_id}`}>
                  <td className="py-1.5 pr-2 font-mono text-[11px] break-all" style={{ color: C.navy, maxWidth: '180px' }}>{r.to}</td>
                  <td className="py-1.5 pr-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium whitespace-nowrap"
                      data-testid={`guard-held-kind-${r.log_id}`}
                      style={r.status === 'throttled'
                        ? { background: 'rgba(232,160,32,0.12)', color: '#A9700A' }
                        : { background: 'rgba(226,75,74,0.1)', color: '#B03230' }}>
                      {r.status === 'throttled' ? 'THROTTLED' : 'BLOCKED'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2" style={{ color: C.navy, maxWidth: '260px' }}>
                    <span className="block truncate">{r.subject}</span>
                    {r.attachment ? <span className="text-[10px]" style={{ color: C.slate }}>{r.attachment}</span> : null}
                  </td>
                  <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>
                    <span className="block truncate" style={{ maxWidth: '280px' }} title={r.detail || r.reason}>{r.detail || r.reason}</span>
                  </td>
                  <td className="py-1.5 pr-2 text-[11px] whitespace-nowrap" style={{ color: C.slate }}>{String(r.at).slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1.5 text-right">
                    {isAdmin && (
                      <Button size="sm" variant="outline" disabled={busy || !r.allowed_now} className="text-[10px] h-6 rounded-sm px-2"
                        title={r.allowed_now ? 'Send this held email now' : 'Allow the recipient first'}
                        data-testid={`guard-release-${r.log_id}`}
                        onClick={() => run(() => api.post(`/email/guard/release/${r.log_id}`), d => `Released to ${d?.to} — ${d?.result?.status}`)}>
                        <Send className="w-3 h-3 mr-1" /> {r.allowed_now ? 'Release' : 'Blocked'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {msg && <p className="text-xs" style={{ color: C.green }} data-testid="guard-msg">{msg}</p>}
      {err && <p className="text-xs" style={{ color: C.red }} data-testid="guard-error">{err}</p>}
    </Card>
  );
};
